import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import TopBar, { ProductTour } from './TopBar';
import Sidebar from './Sidebar';
import { SidebarStateProvider, useSidebarStateContext } from './SidebarStateContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import '../../styles/design-system.css';
import '../../styles/rt2-shell.css';
import '../../styles/rt2-primitives.css';

const AppShellInner = ({
  user,
  onLogout,
  navigateTo: navigateToProp,
  hasProjectContext: hasProjectContextProp,
  projectId: projectIdProp,
  projectName: projectNameProp,
  children,
}) => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const urlProjectId = getProjectIdFromURL();
  const hasProjectContext =
    hasProjectContextProp != null ? hasProjectContextProp : Boolean(urlProjectId);
  const projectId = projectIdProp || urlProjectId || selectedProject?.id;
  const projectName =
    projectNameProp || selectedProject?.name || selectedProject?.project_name || '';

  const navigateTo = useCallback(
    (path = '') => {
      if (typeof navigateToProp === 'function') {
        navigateToProp(path);
        return;
      }
      const workspaceId = getWorkspaceIdFromURL() || currentWorkspace?.id;
      if (!workspaceId) {
        navigate('/dashboard/all-workspaces');
        return;
      }
      const pathBase = String(path || '').split('?')[0].replace(/^\/+|\/+$/g, '');
      // Intentional leave-project destinations show the workspace sidebar.
      const leaveProjectUrl = new Set([
        'projects',
        'all-workspaces',
        'workspace',
        'workspace-home',
      ]).has(pathBase);
      const urlPid = getProjectIdFromURL();

      const includeProjectId = leaveProjectUrl
        ? null
        : urlPid || (hasProjectContext ? projectId : null) || null;

      const subPath =
        pathBase === 'workspace-home' || pathBase === 'workspace' || pathBase === 'overview'
          ? ''
          : path;

      navigate(buildWorkspaceDashboardPath(workspaceId, includeProjectId, subPath));
    },
    [
      navigateToProp,
      getWorkspaceIdFromURL,
      currentWorkspace?.id,
      navigate,
      hasProjectContext,
      projectId,
      getProjectIdFromURL,
    ]
  );

  const {
    locked,
    expanded,
    toggleLock,
    onMouseEnter,
    onMouseLeave,
    collapseHover,
    forceCollapsed,
    setPeek,
  } = useSidebarStateContext();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const { pathname } = useLocation();
  useEffect(() => {
    collapseHover();
  }, [pathname, collapseHover]);

  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    if (!forceCollapsed) setMobileNavOpen(false);
  }, [forceCollapsed]);

  const showExpanded = forceCollapsed ? mobileNavOpen : expanded;

  // Keep portaled full-page overlays (View Tests, etc.) clear of the sidebar.
  useEffect(() => {
    const root = document.documentElement;
    const offset = forceCollapsed
      ? '0px'
      : locked
        ? 'var(--rt-sidebar-w)'
        : 'var(--rt-sidebar-collapsed)';
    root.style.setProperty('--rt-shell-sidebar-offset', offset);
    return () => {
      root.style.removeProperty('--rt-shell-sidebar-offset');
    };
  }, [forceCollapsed, locked]);

  const commands = useMemo(() => {
    const go = (path, label, group) => ({
      label,
      group,
      onSelect: () => navigateTo(path),
    });

    if (hasProjectContext) {
      return [
        go('', 'Project Overview', 'Project'),
        go('requirements', 'Requirements', 'Define'),
        go('test-cases', 'Create Test Case', 'Actions'),
        go('test-suites', 'Create Test Suite', 'Actions'),
        go('test-plans', 'Test Plans', 'Build'),
        go('executions', 'Run Tests', 'Actions'),
        go('reports', 'Open Reports', 'Analyze'),
        go('intelligence', 'Open Analytics', 'Analyze'),
        go('settings', 'Open Settings', 'Configure'),
        go('projects', 'All Projects', 'Workspace'),
        go('all-workspaces', 'All Workspaces', 'Workspace'),
        go('workspace-home', 'Workspace Home', 'Workspace'),
      ];
    }

    return [
      go('workspace-home', 'Workspace Home', 'Workspace'),
      go('projects', 'Projects', 'Workspace'),
      go('settings', 'Open Settings', 'Workspace'),
      go('all-workspaces', 'All Workspaces', 'Workspace'),
    ];
  }, [hasProjectContext, navigateTo]);

  const shellClass = [
    'rt2-shell',
    showExpanded ? 'rt2-shell--expanded' : 'rt2-shell--collapsed',
    locked ? 'rt2-shell--locked' : 'rt2-shell--unlocked',
  ].join(' ');

  return (
    <div className={shellClass}>
      <TopBar
        user={user}
        onLogout={onLogout}
        commands={commands}
        onOpenMobileNav={forceCollapsed ? openMobileNav : undefined}
      />
      <div
        className={`rt2-sidebar-slot${mobileNavOpen ? ' rt2-sidebar-slot--open' : ''}`}
        onMouseEnter={!forceCollapsed ? onMouseEnter : undefined}
        onMouseLeave={!forceCollapsed ? onMouseLeave : undefined}
      >
        <Sidebar
          mode={hasProjectContext ? 'project' : 'workspace'}
          navigateTo={navigateTo}
          projectId={projectId}
          projectName={projectName}
          workspaceName={currentWorkspace?.name || currentWorkspace?.workspace_name || ''}
          expanded={showExpanded}
          locked={locked}
          onToggleLock={toggleLock}
          onMouseEnter={!forceCollapsed ? onMouseEnter : undefined}
          onMouseLeave={!forceCollapsed ? onMouseLeave : undefined}
        />
      </div>
      {mobileNavOpen && (
        <button
          type="button"
          className="rt2-sidebar-backdrop"
          aria-label="Close navigation"
          onClick={closeMobileNav}
        />
      )}
      <main className="rt2-main" data-rt-shell-main>
        {children}
      </main>
      <ProductTour
        mode={hasProjectContext ? 'project' : 'workspace'}
        user={user}
        projectName={projectName}
        onPeekSidebar={setPeek}
      />
    </div>
  );
};

const AppShell = (props) => (
  <SidebarStateProvider>
    <AppShellInner {...props} />
  </SidebarStateProvider>
);

export default AppShell;
